<template>
    <div class="container-fluid">
        <section class="disputes-shell">
            <div class="disputes-hero">
                <div class="disputes-hero-copy">
                    <p class="hero-eyebrow">Dispute Center</p>
                    <h1>Raise, track and manage your dispute tickets</h1>
                    <p class="hero-text">Create a new dispute, attach supporting evidence, and monitor the status of every ticket from one professional workspace.</p>
                </div>
                <div class="disputes-stats">
                    <div class="disputes-stat-card">
                        <span class="stat-label">My Disputes</span>
                        <strong>{{ disputeQty || 0 }}</strong>
                        <span class="stat-note">Tickets submitted from your account</span>
                    </div>
                    <div class="disputes-stat-card disputes-stat-card-soft">
                        <span class="stat-label">Signed In As</span>
                        <strong>{{ firstname }} {{ lastname }}</strong>
                        <span class="stat-note">{{ email_address || 'Customer account' }}</span>
                    </div>
                </div>
            </div>

            <div class="dispute-summary-grid">
                <div class="summary-card">
                    <span class="summary-label">Open Cases</span>
                    <strong>{{ openDisputes }}</strong>
                    <p>Tickets still waiting for action.</p>
                </div>
                <div class="summary-card summary-card-progress">
                    <span class="summary-label">In Progress</span>
                    <strong>{{ inProgressDisputes }}</strong>
                    <p>Cases currently being reviewed.</p>
                </div>
                <div class="summary-card summary-card-resolved">
                    <span class="summary-label">Resolved</span>
                    <strong>{{ resolvedDisputes }}</strong>
                    <p>Issues that have been successfully closed.</p>
                </div>
            </div>

            <div class="account-details dispute-form-card" v-if="addDispute">
                <div class="col-sm-12 dispute-form-inner">
                    <div class="contract-header">
                        <div>
                            Create Dispute
                            <span class="disputes-count">New Ticket</span>
                        </div>
                        <div class="table-heading-meta">
                            <p class="disputes-note">Share the problem clearly so support can respond faster.</p>
                            <button class="header-switch-btn" @click="view_disputes">
                                <i class="fa fa-eye"></i>
                                View My Disputes
                            </button>
                        </div>
                    </div>

                    <div class="form-shell">
                        <form @submit.prevent="createDispute" enctype="multipart/form-data">
                            <div class="form-grid">
                                <div class="form-group">
                                    <label>Dispute Topic</label>
                                    <v-autocomplete required v-model="dispute_subject" label="Select dispute *" :items="disputeSubject" variant="outlined"></v-autocomplete>
                                </div>
                                <div class="form-group">
                                    <label>Service</label>
                                    <v-autocomplete required v-model="contract_list" label="Select service *" :items="contractList" variant="outlined"></v-autocomplete>
                                </div>
                                <div class="form-group">
                                    <label>Priority</label>
                                    <v-autocomplete required v-model="dispute_priority" label="Priority *" :items="priorityType" variant="outlined"></v-autocomplete>
                                </div>
                            </div>

                            <div class="form-group">
                                <label>Description</label>
                                <quill-editor class="quill-container" v-model="dispute_description" :options="editorOptions"></quill-editor>
                            </div>

                            <div class="upload-card">
                                <div class="upload-copy">
                                    <span class="upload-label">Evidence Upload</span>
                                    <p>Add screenshots or images that support your complaint.</p>
                                </div>
                                <input type="file" ref="fileInput" id="image_input" class="file-input" accept="image/*" multiple @change="pickFile" />
                            </div>
                        </form>

                        <div class="form-actions">
                            <button class="btn btn-outline-secondary action-ghost-btn" @click="view_disputes">
                                Cancel
                            </button>
                            <button class="btn submit-btn" :disabled="isDisputeFieldsEnabled || loading" @click="createDispute()" v-if="!loading">
                                Create Ticket
                            </button>
                            <button class="btn submit-btn" type="button" disabled v-if="loading">
                                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="account-details disputes-table-card" v-if="viewDispute">
                <div class="col-sm-12 table table-responsive disputes-table-inner">
                    <div class="contract-header">
                        <div>
                            My Disputes
                            <span class="disputes-count">{{ disputeQty || 0 }}</span>
                        </div>
                        <div class="table-heading-meta">
                            <p class="disputes-note">Track your ticket priority, current status and dispute history.</p>
                            <button class="header-switch-btn" @click="add_products">
                                <i class="fa fa-plus-square"></i>
                                Create New Dispute
                            </button>
                        </div>
                    </div>

                    <div class="table table-responsive table-sm disputes-table-wrap">
                        <v-data-table :headers="header" :items="desserts" dense class="disputes-data-table table-responsive">
                            <template v-slot:item.ticketid="{ item }">
                                <div class="a-text">{{ item.ticketid }}</div>
                            </template>
                            <template v-slot:item.disputetype="{ item }">
                                <div class="a-text">{{ item.disputetype }}</div>
                            </template>
                            <template v-slot:item.contract="{ item }">
                                <div class="a-text">{{ item.contract }}</div>
                            </template>
                            <template v-slot:item.description="{ item }">
                                <div class="a-text" v-html="item.description"></div>
                            </template>
                            <template v-slot:item.priority="{ item }">
                                <div class="a-text">
                                    <span class="priority-chip high" v-if="item.priority == 'High'">{{ item.priority }}</span>
                                    <span class="priority-chip medium" v-else-if="item.priority == 'Medium'">{{ item.priority }}</span>
                                    <span class="priority-chip low" v-else-if="item.priority == 'Low'">{{ item.priority }}</span>
                                </div>
                            </template>
                            <template v-slot:item.status="{ item }">
                                <div class="a-text">
                                    <span class="status-chip open" v-if="item.status == 1">Open</span>
                                    <span class="status-chip progress" v-else-if="item.status == 2">In Progress</span>
                                    <span class="status-chip resolved" v-else-if="item.status == 3">Resolved</span>
                                </div>
                            </template>
                            <template v-slot:no-data>
                                <div class="disputes-empty-state">
                                    <i class="fa fa-life-ring"></i>
                                    <h4>No disputes found</h4>
                                    <p>Your submitted dispute tickets will appear here once you create one.</p>
                                </div>
                            </template>
                        </v-data-table>
                    </div>
                </div>
            </div>
        </section>

        <p>&nbsp;</p>
        <page-loader :is-loading="isLoading"></page-loader>
    </div>
</template>
    
      
    <script>
    import 'quill/dist/quill.snow.css';
    import {
        quillEditor
    } from 'vue-quill-editor';
    
    import swal from 'sweetalert2';
    import PageLoader from '../components/PageLoader.vue'
    export default {
    
        name: "ProductPage",
        components: {
            quillEditor,
            PageLoader
        },
    
        data() {
            return {
    
                isLoading: false,
                isRightImage: false,
                isLeftImage: false,
                isBackImage: false,
                is_transaction_fee: true,
                is_transaction_fee_hidden: false,
                user_id: '',
                firstname: '',
                lastname: '',
                bank_name: '',
                account_name: '',
                account_number: '',
                email_address: '',
                phone_number: '',
    
                loading: false,
                loading1: false,
                loading2: false,
                loading3: false,

                selectedFile: [],
                addDispute: false,
                viewDispute: true,
                editorOptions: {
                    placeholder: 'Write something...',
                },

                img_url: process.env.VUE_APP_IMAGE_URL,
                desserts: [],
                header: [
                   
                    {
                        value: 'ticketid',
                        text: '#Ticket number'
                    },
                    {
                        value: 'disputetype',
                        text: 'Dispute topic'
                    },
                    {
                        value: 'contract',
                        text: 'Service'
                    },
                                       
                    {
                        value: 'description',
                        text: 'Description'
                    },
                    {
                        value: 'priority',
                        text: 'Priority'
                    },
                    {
                        value: 'status',
                        text: 'Status'
                    },
                ],
                dispute_subject: '',
                disputeSubject: [],
                contract_list:'',
                contractList: [
                    {
                    text: 'Data',
                    value: 'Data'
                },
                {
                    text: 'Airtime',
                    value: 'Airtime'
                },
                {
                    text: 'Electricity',
                    value: 'Electricity'
                },
                {
                    text: 'Cable TV',
                    value: 'CableTV'
                },
                ],
                dispute_priority: '',
                priorityType: [{
                    text: 'High',
                    value: 'High'
                },
                {
                    text: 'Medium',
                    value: 'Medium'
                },
                {
                    text: 'Low',
                    value: 'Low'
                },
            ],
                dispute_description: '',
                disputeQty: '',

            };
        },
    
        beforeMount() {
    
        },
    
        mounted() {
    
            this.firstname = localStorage.getItem("firstname");
            this.lastname = localStorage.getItem("lastname");
            this.phone_number = localStorage.getItem("phone");
            this.email_address = localStorage.getItem("email");
            this.user_id = localStorage.getItem("userid");
    
            this.bank_name = localStorage.getItem("bank_name");
            this.account_name = localStorage.getItem("account_name");
            this.account_number = localStorage.getItem("account_number");
            this.user_role = localStorage.getItem("user_role");
    
            //this.fetchPaidContractByUser()
            this.fetch_dispute_subject()
            this.fetch_disputes()
        },
    
        created() {
    
        },
    
        computed: {
    
            isDisputeFieldsEnabled() {
                return (
                    this.dispute_subject === '' ||
                    this.contract_list === '' ||
                    this.dispute_priority === '' ||
                    this.dispute_description === ''
                );
            },
    
            indexSn() {
                return this.desserts.map((d, index) => ({
                    ...d,
                    sn: index + 1
                }))
            },

            openDisputes() {
                return this.desserts.filter((item) => Number(item.status) === 1).length
            },

            inProgressDisputes() {
                return this.desserts.filter((item) => Number(item.status) === 2).length
            },

            resolvedDisputes() {
                return this.desserts.filter((item) => Number(item.status) === 3).length
            },
    
        },
    
        methods: {
                
            async fetch_dispute_subject() {
                try {
    
                    const response = await this.$http.get('/fetch-dispute-subject', {})
                    response.data.data.forEach(el => {
                        this.disputeSubject.push({
                            value: el.subjectid,
                            text: el.subjects,
                        })
                    });
    
                } catch (err) {
                    console.log(err);
                }
            },

            async createDispute() {
                try {
    
                    if (!this.dispute_subject) {
                        swal.fire({
                            icon: 'warning',
                            title: 'Warning',
                            width: 450,
                            height: 5,
                            text: 'Please select dispute',
                        })
                        this.loading = false
                        return;
                    }
                    if (!this.contract_list) {
                        swal.fire({
                            icon: 'warning',
                            title: 'Warning',
                            width: 450,
                            height: 5,
                            text: 'Please select service',
                        })
                        this.loading = false
                        return;
                    }
                    if (!this.dispute_priority) {
                        swal.fire({
                            icon: 'warning',
                            title: 'Warning',
                            width: 450,
                            height: 5,
                            text: 'Please select priority',
                        })
                        this.loading = false
                        return;
                    }

                    if (!this.dispute_description) {
                        swal.fire({
                            icon: 'warning',
                            title: 'Warning',
                            width: 450,
                            height: 5,
                            text: 'Please describe your problem',
                        })
                        this.loading = false
                        return;
                    }

                    // if (!this.selectedFile) {
                    //     swal.fire({
                    //         icon: 'warning',
                    //         title: 'Warning',
                    //         width: 450,
                    //         height: 5,
                    //         text: 'Please attach contract document',
                    //     })
                    //     this.loading = false
                    //     return;
                    // }
                                      
                    this.loading = true
    
                    const formData = new FormData();
    
                    //Loop through each selected file and append it to the formData
                    for (let i = 0; i < this.selectedFile.length; i++) {
                        formData.append('images', this.selectedFile[i]);
                    }
    
                    formData.append('dispute_subject', this.dispute_subject),
                    formData.append('contract_list', this.contract_list),
                    formData.append('dispute_priority', this.dispute_priority),
                    formData.append('dispute_description', this.dispute_description),
                    formData.append('user_id', this.user_id),
    
                    this.response = await this.$http.post('/create-dispute', formData
    
                    );
                    this.loading = false
    
                    if (!this.response.data.success) {
                        swal.fire({
                            icon: 'error',
                            title: 'Error',
                            width: 450,
                            height: 5,
                            text: this.response.data.message,
                        })
                        return;
                    } else {
                        swal.fire({
                            icon: 'success',
                            title: 'Success',
                            width: 450,
                            height: 5,
                            text: this.response.data.message,
                        })
                       
                        this.addDispute = false
                        this.viewDispute = true
                                               
                        this.dispute_subject = ''
                        this.contract_list = ''
                        this.dispute_priority = ''
                        this.dispute_description = ''
                        this.selectedFile = []
                        if (this.$refs.fileInput) {
                            this.$refs.fileInput.value = null
                        }
                        this.fetch_disputes()
                        return;
                    }
                } catch (err) {
                    console.log(err);
                }
            },
           
            copyToClipboard(link) {
               // var copyText = document.getElementById("myInput");
                navigator.clipboard.writeText(link).then(function() {
                    alert("Contract link copied to clipboard!");
                }).catch(function(err) {
                    console.error('Failed to copy: ', err);
                });
            },
    
            copyToClipboard1(link) {
               // var copyText = document.getElementById("myInput");
                navigator.clipboard.writeText(link).then(function() {
                    alert("Contract link copied to clipboard!");
                }).catch(function(err) {
                    console.error('Failed to copy: ', err);
                });
            },

            async fetchPaidContractByUser() {
                try {
                    this.isLoading = true
                    this.desserts = []
                    const response = await this.$http.post('/fetch-paid-contracts', {
                        user_id: this.user_id
                    })
                    this.isLoading = false
                
                    response.data.data.forEach(el => {
                        this.contractList.push({
                        value: el.contractid,
                        text: el.contract_title,
                        })
                    });

                } catch (err) {
                    console.log(err);
                }
            },

            async fetch_disputes() {
                try {
    
                    this.desserts = []
                    const response = await this.$http.post('/fetch-dispute-byuser', {
                        user_id: this.user_id
                    })
                    const dispute_list = response.data.data
                    this.disputeQty = dispute_list.length
                    dispute_list.forEach(el => {
                        this.desserts.push({
                            ticketid: el.ticket_id,
                            disputetype: el.subjects,
                            contract: el.contract_title,
                            description: el.content,
                            priority: el.priority,
                            status: el.dispute_status,
                        })
                    });
    
                } catch (err) {
                    console.log(err);
                }
            },
        
            pickFile() {
                this.selectedFile = this.$refs.fileInput ? this.$refs.fileInput.files : [];
            },
           
            view_disputes() {
                this.viewDispute = true
                this.addDispute = false
            },
            add_products() {
                this.viewDispute = false
                this.addDispute = true
            }
    
        }
    
    }
    </script>
      
<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;700&display=swap');

.container-fluid {
    background:
        radial-gradient(circle at top left, rgba(36, 86, 43, 0.12), transparent 24%),
        linear-gradient(180deg, #f4f8f5 0%, #edf3ee 100%);
    border-radius: 10px;
    padding: 1.25rem 1rem 2rem;
    min-height: 100vh;
}

.disputes-shell {
    max-width: 1240px;
    margin: 0 auto;
}

.disputes-hero {
    display: grid;
    grid-template-columns: 1.35fr 1fr;
    gap: 1.2rem;
    margin-bottom: 1.25rem;
    align-items: stretch;
}

.disputes-hero-copy {
    padding: 2rem;
    border-radius: 28px;
    background: linear-gradient(135deg, #16372d 0%, #24562B 58%, #3d7c50 100%);
    box-shadow: 0 20px 45px rgba(11, 39, 24, 0.1);
    color: #fff;
    font-family: Poppins;
}

.hero-eyebrow {
    margin: 0 0 0.65rem;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: rgba(255, 255, 255, 0.72);
}

.disputes-hero-copy h1 {
    margin: 0;
    font-size: 32px;
    line-height: 1.15;
    font-weight: 700;
}

.hero-text {
    margin: 0.9rem 0 0;
    max-width: 560px;
    font-size: 14px;
    line-height: 1.75;
    color: rgba(255, 255, 255, 0.84);
}

.disputes-stats {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    padding: 1rem;
    border-radius: 28px;
    background: rgba(255, 255, 255, 0.72);
    box-shadow: 0 20px 45px rgba(11, 39, 24, 0.07);
    backdrop-filter: blur(10px);
}

.disputes-stat-card {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 176px;
    padding: 1.2rem;
    border-radius: 22px;
    background: linear-gradient(180deg, #ffffff 0%, #f4f8f5 100%);
    border: 1px solid rgba(22, 55, 45, 0.08);
    font-family: Poppins;
}

.disputes-stat-card-soft {
    background: linear-gradient(180deg, #f5fbf6 0%, #eef7f0 100%);
}

.stat-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #6f8579;
}

.disputes-stat-card strong {
    margin: 0.85rem 0 0.55rem;
    font-size: 28px;
    line-height: 1.2;
    color: #16372d;
    word-break: break-word;
}

.stat-note {
    font-size: 12px;
    line-height: 1.6;
    color: #66776f;
}

.dispute-summary-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
    margin-bottom: 1.25rem;
}

.summary-card {
    padding: 1.15rem 1.2rem;
    border-radius: 22px;
    background: linear-gradient(180deg, #ffffff 0%, #f7faf8 100%);
    border: 1px solid rgba(22, 55, 45, 0.08);
    box-shadow: 0 18px 38px rgba(11, 39, 24, 0.06);
    font-family: Poppins;
}

.summary-card-progress {
    background: linear-gradient(180deg, #f7faff 0%, #edf4ff 100%);
}

.summary-card-resolved {
    background: linear-gradient(180deg, #f5fbf6 0%, #edf7ef 100%);
}

.summary-label {
    display: block;
    margin-bottom: 0.65rem;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #6f8579;
}

.summary-card strong {
    display: block;
    margin-bottom: 0.45rem;
    font-size: 30px;
    line-height: 1.1;
    color: #16372d;
    font-family: Poppins;
}

.summary-card p {
    margin: 0;
    font-size: 12px;
    line-height: 1.65;
    color: #66776f;
    font-family: Poppins;
}

.account-details {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    border-radius: 24px;
    background: #fff;
    width: 100%;
}

.dispute-form-card,
.disputes-table-card {
    padding: 1.15rem;
    border: 1px solid rgba(22, 55, 45, 0.07);
    box-shadow: 0 20px 45px rgba(11, 39, 24, 0.07);
}

.contract-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    background: linear-gradient(135deg, #16372d 0%, #24562B 100%);
    color: #fff;
    border-radius: 20px;
    padding: 1rem 1.15rem;
    font-size: 14px;
    font-weight: 700;
    font-family: Poppins;
    margin-bottom: 0.9rem;
}

.disputes-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    padding: 0 0.7rem;
    margin-left: 0.55rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.16);
    font-size: 12px;
    font-weight: 700;
}

.table-heading-meta {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
}

.disputes-note {
    margin: 0;
    font-size: 12px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.82);
}

.header-switch-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    border: 0;
    border-radius: 999px;
    padding: 0.55rem 0.95rem;
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    font-family: Poppins;
}

.form-shell {
    padding: 1.2rem;
    border-radius: 20px;
    background: linear-gradient(180deg, #ffffff 0%, #f8fbf8 100%);
    border: 1px solid #e7efea;
}

.form-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.45rem;
    font-size: 12px;
    font-family: Poppins;
    font-weight: 600;
    color: #315244;
}

.quill-container {
    height: 200px;
    font-family: Poppins;
}

.upload-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-top: 1.2rem;
    padding: 1rem 1.1rem;
    border-radius: 18px;
    background: #f4f8f5;
    border: 1px dashed #bfd0c4;
}

.upload-label {
    display: inline-block;
    margin-bottom: 0.3rem;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #24562B;
}

.upload-copy p {
    margin: 0;
    font-size: 12px;
    line-height: 1.6;
    color: #66776f;
}

.file-input {
    width: min(100%, 320px);
    font-size: 12px;
    font-family: Poppins;
}

.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1.25rem;
}

.action-ghost-btn {
    min-width: 120px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    font-family: Poppins;
}

.submit-btn {
    min-width: 150px;
    border: 0;
    border-radius: 999px;
    padding: 0.75rem 1.1rem;
    background: #24562B;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    font-family: Poppins;
    box-shadow: 0 14px 24px rgba(36, 86, 43, 0.15);
}

.submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.disputes-table-wrap {
    margin: 0;
    border: 1px solid #e7efea;
    border-radius: 18px;
    overflow: hidden;
    background: #fff;
}

.a-text {
    font-size: 11px;
    font-family: Poppins;
}

.priority-chip,
.status-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 88px;
    padding: 0.35rem 0.7rem;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    font-family: Poppins;
}

.priority-chip.high {
    background: rgba(199, 42, 42, 0.12);
    color: #b42318;
}

.priority-chip.medium {
    background: rgba(206, 145, 24, 0.14);
    color: #a86700;
}

.priority-chip.low {
    background: rgba(36, 86, 43, 0.12);
    color: #24562B;
}

.status-chip.open {
    background: rgba(199, 42, 42, 0.12);
    color: #b42318;
}

.status-chip.progress {
    background: rgba(28, 100, 242, 0.12);
    color: #1d4ed8;
}

.status-chip.resolved {
    background: rgba(36, 86, 43, 0.12);
    color: #24562B;
}

.disputes-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    padding: 2.5rem 1.25rem;
    text-align: center;
    font-family: Poppins;
    color: #60746a;
}

.disputes-empty-state i {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #eef6ef;
    color: #24562B;
    font-size: 22px;
}

.disputes-empty-state h4 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #16372d;
}

.disputes-empty-state p {
    margin: 0;
    max-width: 420px;
    font-size: 13px;
    line-height: 1.7;
}

:deep(.disputes-data-table .v-data-table__wrapper table) {
    min-width: 920px;
}

:deep(.disputes-data-table thead th) {
    background: #f4f8f5;
    color: #5d7166;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-family: Poppins;
    border-bottom: 1px solid #e6eeea;
}

:deep(.disputes-data-table tbody td) {
    padding-top: 14px !important;
    padding-bottom: 14px !important;
    border-bottom: 1px solid #eef3ef;
    background: #fff;
    vertical-align: middle;
}

:deep(.disputes-data-table tbody tr:hover td) {
    background: #f8fbf8 !important;
}

:deep(.disputes-data-table .v-data-footer) {
    border-top: 1px solid #eef3ef;
}

@media screen and (max-width: 991px) {
    .container-fluid {
        padding: 1rem 0.85rem 1.5rem;
    }

    .disputes-hero {
        grid-template-columns: 1fr;
    }

    .disputes-hero-copy {
        padding: 1.45rem;
    }

    .disputes-hero-copy h1 {
        font-size: 26px;
    }

    .disputes-stats,
    .dispute-summary-grid,
    .form-grid {
        grid-template-columns: 1fr;
    }

    .disputes-stat-card {
        min-height: auto;
    }

    .contract-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .dispute-form-card,
    .disputes-table-card {
        padding: 0.9rem;
    }
}

@media screen and (max-width: 768px) {
    .container-fluid {
        padding: 1rem 0.75rem 1.25rem;
    }

    .contract-header {
        border-radius: 16px;
        padding: 0.9rem 1rem;
        font-size: 12px !important;
    }

    .table-heading-meta {
        align-items: flex-start;
    }

    .disputes-count {
        margin-left: 0.35rem;
    }

    .disputes-note {
        line-height: 1.5;
    }

    .disputes-table-wrap,
    .form-shell {
        border-radius: 14px;
    }

    .upload-card,
    .form-actions {
        flex-direction: column;
        align-items: stretch;
    }

    .submit-btn,
    .action-ghost-btn,
    .header-switch-btn {
        width: 100%;
        justify-content: center;
    }

    .account-details {
        border-radius: 18px;
        width: 100%;
    }
}
</style>
    
